import { Button, Header, Icon, LoadingSpinner, OtpInput, TextInput } from '@components/Generic'
import { observer } from 'mobx-react'
import { useContext, useEffect, useState } from 'react'
import { StoreContext } from '@store/Root.store'
import { actionReport } from '@methods/actionReport'
import { axiosInstance } from '@methods/axiosConfig'
import type { AxiosError } from 'axios'
import { useInterval } from 'usehooks-ts'
import { forceRedirect } from '@/methods/forceRedirect'
import { devLog } from '@/methods/devLog'
import { useKeyboardAwareViewport } from '@/hooks/useKeyboardAwareViewport'
import '@/components/Generic/OtpInput/OtpInput.css'
import { API_URL } from '@constants/apiUrl'
import { cx } from '@/utils/cx'

const toggleEyeIcon = (eye: boolean) => {
  return eye ? (
    <svg
      aria-hidden="true"
      width="25"
      height="24"
      viewBox="0 0 25 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12.4996 16.8516C8.83658 16.8516 6.50121 13.9936 5.53205 12.4873C5.33853 12.1865 5.33853 11.8166 5.53205 11.5159C6.50121 10.0096 8.83658 7.15156 12.4996 7.15156C16.1626 7.15156 18.498 10.0096 19.4671 11.5159C19.6606 11.8166 19.6606 12.1865 19.4671 12.4873C18.498 13.9936 16.1626 16.8516 12.4996 16.8516ZM4.43879 13.1907C5.45047 14.7631 8.12454 18.1516 12.4996 18.1516C16.8746 18.1516 19.5487 14.7631 20.5604 13.1907C21.0296 12.4615 21.0296 11.5417 20.5604 10.8125C19.5487 9.24007 16.8746 5.85156 12.4996 5.85156C8.12454 5.85156 5.45047 9.24007 4.43879 10.8125C3.96962 11.5417 3.96962 12.4615 4.43879 13.1907ZM9.18292 12.0016C9.18292 13.8603 10.6491 15.4016 12.4996 15.4016C14.35 15.4016 15.8163 13.8603 15.8163 12.0016C15.8163 10.1428 14.35 8.60156 12.4996 8.60156C10.6491 8.60156 9.18292 10.1428 9.18292 12.0016ZM12.4996 14.1016C11.4045 14.1016 10.4829 13.1804 10.4829 12.0016C10.4829 10.8228 11.4045 9.90156 12.4996 9.90156C13.5947 9.90156 14.5163 10.8228 14.5163 12.0016C14.5163 13.1804 13.5947 14.1016 12.4996 14.1016Z"
        fill="currentColor"
      />
    </svg>
  ) : (
    <svg
      aria-hidden="true"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M18.8605 5.85835C19.1143 6.11219 19.1143 6.52375 18.8605 6.77759L6.77971 18.8583C6.52587 19.1122 6.11431 19.1122 5.86047 18.8583C5.60663 18.6045 5.60663 18.193 5.86047 17.9391L7.24924 16.5503C5.62167 15.4494 4.52287 13.9942 3.9399 13.0882C3.4708 12.3592 3.47039 11.439 3.93966 10.7096C4.95134 9.13723 7.62541 5.74873 12.0005 5.74873C13.8444 5.74873 15.3913 6.35452 16.6321 7.16749L17.9412 5.85835C18.1951 5.60451 18.6066 5.60451 18.8605 5.85835ZM15.6899 8.10967C14.6637 7.48448 13.4363 7.04873 12.0005 7.04873C8.33745 7.04873 6.00208 9.90673 5.03292 11.413C4.8395 11.7136 4.83952 12.0839 5.03314 12.3848C5.59505 13.258 6.6517 14.638 8.18814 15.6114L9.48206 14.3175C8.90258 13.6835 8.54968 12.8315 8.54968 11.8988C8.54968 9.96407 10.0756 8.36128 11.9997 8.36128C12.9467 8.36128 13.8008 8.75253 14.4209 9.37872L15.6899 8.10967ZM13.5017 10.2979C13.1113 9.90098 12.5805 9.66128 11.9997 9.66128C10.831 9.66128 9.84968 10.644 9.84968 11.8988C9.84968 12.4775 10.06 13.0016 10.4028 13.3968L13.5017 10.2979ZM17.8697 8.99114C18.1367 8.7512 18.5477 8.77316 18.7877 9.04018C19.3407 9.65566 19.7646 10.2492 20.0604 10.7089C20.5294 11.4378 20.5303 12.3584 20.0609 13.0879C19.0492 14.6603 16.3751 18.0488 12.0001 18.0488C11.4221 18.0488 10.8717 17.9892 10.3497 17.8816C9.99811 17.8091 9.77184 17.4653 9.84431 17.1137C9.91679 16.7622 10.2606 16.5359 10.6122 16.6084C11.0496 16.6985 11.5121 16.7488 12.0001 16.7488C15.6631 16.7488 17.9985 13.8908 18.9676 12.3845C19.1609 12.0841 19.1609 11.7135 18.9672 11.4124C18.7006 10.9981 18.318 10.4625 17.8207 9.90907C17.5808 9.64205 17.6027 9.23108 17.8697 8.99114ZM15.1971 12.0164C15.5515 12.0736 15.7924 12.4073 15.7351 12.7617C15.4949 14.2491 14.3434 15.4428 12.857 15.675C12.5023 15.7304 12.1698 15.4878 12.1144 15.1331C12.059 14.7784 12.3017 14.446 12.6563 14.3906C13.5622 14.2491 14.2972 13.5112 14.4518 12.5545C14.509 12.2001 14.8427 11.9592 15.1971 12.0164Z"
        fill="currentColor"
      />
    </svg>
  )
}

interface PollingResponse {
  status: 'ready' | 'not-ready'
}

const defaultOtpLength = 6

export const LtqcSignPage = observer(() => {
  const viewport = useKeyboardAwareViewport()
  const store = useContext(StoreContext)
  const { isMobile } = store.AppState
  const { currentScenarioId, context } = store.ScenarioState
  const { ltqcSignPage: trans } = store.TranslationsState.translationsNew

  const [certificatePin, setCertificatePin] = useState('')
  const [type, setType] = useState<'text' | 'password'>('password')
  const [challengeCode, setChallengeCode] = useState('')
  const [btnLoading, setBtnLoading] = useState(false)
  const [step, setStep] = useState(0)
  const [shouldPoll, setShouldPoll] = useState(true)
  const [isChecking, setIsChecking] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleError = (error: unknown) => {
    const axiosError = error as AxiosError<{ error: string }>
    const responseObject = axiosError?.response

    if (responseObject) {
      const errorCode = responseObject.data.error

      if (step === 1 || errorCode === 'Signature.InvalidOtpCode') {
        setErrorMessage(trans.errors[errorCode as keyof typeof trans.errors] || null)

        setChallengeCode('')
        setCertificatePin('')
        step !== 1 && setStep(1)
        setBtnLoading(false)
      } else {
        forceRedirect(`${API_URL}/electronic-id/redirect-on-finish?result=fail&error=${errorCode}`)
      }
    } else {
      forceRedirect(`${API_URL}/electronic-id/redirect-on-finish?result=fail&error=`)
    }
  }

  const handleProceed = async () => {
    if (currentScenarioId !== 'localTests') {
      setBtnLoading(true)

      try {
        await axiosInstance.post(
          `${API_URL}/electronic-id/workflow/sign`,
          {
            challengeCode,
            certificatePin,
          },
          {
            withCredentials: true,
          }
        )

        forceRedirect(`${API_URL}/electronic-id/redirect-on-finish?result=complete`)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        devLog(e)

        handleError(e)
      }
    }
  }

  const handleSendOtp = async () => {
    if (currentScenarioId !== 'localTests') {
      setBtnLoading(true)

      try {
        await axiosInstance.post(
          `${API_URL}/electronic-id/workflow/request-signature`,
          {},
          {
            withCredentials: true,
          }
        )
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        devLog(e)

        handleError(e)
      }
      setBtnLoading(false)
    }

    setStep((step) => step + 1)
  }

  const checkStatus = async (): Promise<PollingResponse | undefined> => {
    setIsChecking(true)

    try {
      const res = await axiosInstance.get<PollingResponse>(
        `${API_URL}/electronic-id/are-documents-ready-to-be-signed`,
        {
          withCredentials: true,
        }
      )

      if (res.data.status && res.data.status === 'ready') {
        setShouldPoll(false)
      }
      return res.data
    } catch (e) {
      devLog(e)
    } finally {
      setIsChecking(false)
    }
  }

  const handleCertificatePinOnBlur = () => {
    if (certificatePin) {
      actionReport({
        type: 'event.onboarding-web.eidv.USER_ENTERED_CERTIFICATE_PIN',
        payload: {},
      })
    }
  }

  const handleChallengeCodeOnBlur = () => {
    if (challengeCode) {
      actionReport({
        type: 'event.onboarding-web.eidv.USER_ENTERED_CHALLENGE_CODE',
        payload: {},
      })
    }
  }

  useEffect(() => {
    if (!shouldPoll) {
      setStep((step) => step + 1)
    }
  }, [shouldPoll])

  useInterval(
    () => {
      if (!isChecking) {
        checkStatus()
      }
    },
    shouldPoll ? 1000 : null
  )

  const renderCurrentStep = () => {
    if (!context) return <LoadingSpinner />

    switch (step) {
      case 0:
        return (
          <>
            <LoadingSpinner />
            <p className="generic__paragraph-centered">{trans.addingSignature}...</p>
          </>
        )
      case 1:
        return (
          <>
            <Header>{trans.ConfirmPassword.header}</Header>

            {errorMessage && (
              <p className="generic__paragraph-centered">
                <p className="generic__error-message">{errorMessage}</p>
              </p>
            )}

            <div className="generic__text-input-wrap">
              <TextInput
                onKeyPress={!!certificatePin && !!certificatePin.length ? handleSendOtp : undefined}
                type={type}
                value={certificatePin}
                onChange={(value) => {
                  setCertificatePin(value)
                }}
                onBlur={handleCertificatePinOnBlur}
                id="certificatePin-input"
                eyeIcon={
                  <div
                    className="text-input__eye-icon"
                    onClick={() => setType(type === 'password' ? 'text' : 'password')}
                  >
                    {toggleEyeIcon(type === 'password')}
                  </div>
                }
              />
            </div>

            <div
              className="layout__content-container-bottom-fixed"
              style={
                viewport?.keyboardHeight
                  ? { top: 'unset', bottom: `${viewport.keyboardHeight}px` }
                  : undefined
              }
            >
              {!isMobile ? <div className="layout__content-container-bottom-left" /> : <></>}
              <Button
                onClick={() => handleSendOtp()}
                disabled={!(certificatePin && certificatePin.length)}
                id="page-submit"
              >
                {btnLoading && <LoadingSpinner inButton />}
                <span
                  className={cx(
                    'generic__button-text',
                    btnLoading && 'generic__button-text--loading'
                  )}
                >
                  {trans.ConfirmPassword.proceed}
                </span>
              </Button>
            </div>
          </>
        )
      case 2:
        return (
          <>
            <Header>{trans.ConfirmOtp.header}</Header>

            <p className="generic__paragraph">{trans.ConfirmOtp.description}</p>

            <div className="generic__text-input-wrap">
              {context?.otpChallengeCodeConfiguration &&
              context?.otpChallengeCodeConfiguration.charset === 'Numeric' ? (
                <div className="otp-input__code-input-wrapper">
                  <OtpInput
                    value={challengeCode}
                    valueLength={context?.otpChallengeCodeConfiguration.length || defaultOtpLength}
                    onChange={(val) => {
                      setChallengeCode(val)
                    }}
                    className="otp-input__container--fit-width otp-input__container--compact otp-input__container--h-50"
                  />
                </div>
              ) : (
                <TextInput
                  onKeyPress={challengeCode && challengeCode.length ? handleProceed : undefined}
                  type="text"
                  value={challengeCode}
                  onChange={(value) => {
                    setChallengeCode(value)
                  }}
                  onBlur={handleChallengeCodeOnBlur}
                  id="ChallengeCode-input"
                  autoComplete={'one-time-code'}
                />
              )}
            </div>

            <div
              className="layout__content-container-bottom-fixed"
              style={
                viewport?.keyboardHeight
                  ? { top: 'unset', bottom: `${viewport.keyboardHeight}px` }
                  : undefined
              }
            >
              {!isMobile ? <div className="layout__content-container-bottom-left" /> : <></>}
              <Button
                onClick={() => handleProceed()}
                disabled={!(challengeCode && challengeCode.length)}
                id="page-submit"
              >
                {btnLoading && <LoadingSpinner inButton />}
                <span
                  className={cx(
                    'generic__button-text',
                    btnLoading && 'generic__button-text--loading'
                  )}
                >
                  {trans.ConfirmOtp.proceed}
                </span>
              </Button>
            </div>
          </>
        )
      default:
        return <></>
    }
  }

  return <div className="page-container">{renderCurrentStep()}</div>
})
